import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { emitter } from '../../events/bridge-events-emitter';
import { isTransportEnvelope } from '../bridge-api-transport-envelope';
import { isSameOrigin, isTop } from './browser-apis';

/* eslint-disable no-console */

/**
 * A browser-compatible transport protocol, used for communicating with cross-origin targets.
 *
 * Leverages {@link window.postMessage} to communicate with such targets, with narrowing to a specific `iframe`
 * window achieved by utilising the {@link RPCMessage.params.receiverId} as an expected data attribute value on
 * an <iframe> which elects itself to be a valid communication target.
 *
 * I.e., a cross-origin <iframe> which is to be communicated with using a `receiverId` of `my-app` must declare
 * a data attribute on its <iframe> markup with `data-rovo-receiver-id="my-app"` for {@link BrowserTransport} to work.
 */
export var BrowserTransport = /*#__PURE__*/function () {
  function BrowserTransport(source) {
    _classCallCheck(this, BrowserTransport);
    /**
     * A mapping of receiver IDs to known targets.
     *
     * Communication will ultimately take place with this target, via the {@link HTMLIFrameElement.contentWindow} property.
     */
    _defineProperty(this, "targets", new Map());
    /**
     * Handles incoming messages received via {@link window.postMessage}.
     */
    _defineProperty(this, "handler", undefined);
    this.source = source;
    emitter().emit({
      type: 'transport-ready',
      transportType: this.source
    });
  }

  /**
   * Check to see if this message can be sent to its intended cross-origin target currently.
   *
   * @description Determined by availability of the target window in the DOM.
   * @description If this method does not yield true, the message will be buffered & tried again later.
   *
   * @param message intended RPC message for the receiver
   * @returns true if the transport target is queryable via DOM, otherwise false
   */
  return _createClass(BrowserTransport, [{
    key: "canSend",
    value: function canSend(message) {
      // passthrough for same-origin communication
      if (isSameOrigin(window)) {
        return true;
      }

      // otherwise find the cross-origin target & communicate with it
      var target = this.findTarget(message.params.receiverId);
      return Boolean(target);
    }

    /**
     * Sends a cross-origin message using {@link window.postMessage}.
     * @param message intended RPC message for the receiver
     * @returns void
     */
  }, {
    key: "send",
    value: function send(message) {
      var target = this.findTarget(message.params.receiverId);
      if (!target) {
        emitter().emit({
          type: 'transport-error',
          transportType: this.source,
          direction: 'outbound',
          messageId: message.id,
          error: {
            message: "Transport target not found for receiver: ".concat(message.params.receiverId),
            code: 'TARGET_NOT_FOUND'
          }
        });
        return;
      }
      var envelope = {
        version: 1,
        source: this.source,
        message: message
      };
      emitter().emit({
        type: 'transport-message-sent',
        transportType: this.source,
        direction: 'outbound',
        messageId: message.id,
        message: _objectSpread({}, envelope)
      });
      target.postMessage(envelope, '*');
    }

    /**
     * Handles incoming events from other bridge API clients.
     * @param handler responds to a given RPC message from a remote client.
     */
  }, {
    key: "onMessage",
    value: function onMessage(handler) {
      var _this = this;
      // do not allow overriding of handlers.
      if (this.handler) {
        return;
      }

      // stable reference so we can destroy it later
      this.handler = function (envelope) {
        // always validate first - if invalid, ignore
        if (!isTransportEnvelope(envelope.data)) {
          return;
        }

        // ensure message is not from the current window to itself
        if (envelope.data.source === _this.source) {
          return;
        }
        emitter().emit({
          type: 'transport-message-received',
          transportType: _this.source,
          direction: 'inbound',
          messageId: envelope.data.message.id,
          transportData: {
            source: envelope.data.source,
            version: envelope.data.version,
            message: envelope.data.message
          }
        });
        handler(envelope.data.message);
      };
      window.addEventListener('message', this.handler);
    }

    /**
     * Clears all relevant state.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      if (!this.handler) {
        return;
      }

      // emit transport destroyed event
      emitter().emit({
        type: 'transport-destroyed',
        transportType: this.source
      });
      window.removeEventListener('message', this.handler);
    }

    /**
     * Find a target for the given receiver in one of three ways:
     * - Using the existing mapping of known targets ({@link #targets})
     * - Using a DOM query for a cross-origin `iframe`, based on a specific DOM selector contract
     * - Using {@link window.top} if communicating cross-origin from inside of a frame
     *
     * @description If a target is find with one of the latter strategies, it is added to {@link #targets} for subsequent reads.
     *
     * @param receiverId intended target of the current message
     * @returns target if found, otherwise undefined
     */
  }, {
    key: "findTarget",
    value: function findTarget(receiverId) {
      // if existing, return
      var target = this.targets.get(receiverId);
      if (target) {
        return target;
      }
      // assumption: top -> frame comms
      else if (isTop(window)) {
        var _target = this.getCrossOriginTarget(receiverId);
        if (_target) {
          this.targets.set(receiverId, _target);
          return _target;
        }
      }
      // assumption: frame -> top comms
      else if (window.top) {
        this.targets.set(receiverId, window.top);
        return window.top;
      }
      // no target found
      else {
        console.warn("transport target not found for message with intended recipient: ".concat(receiverId));
      }
    }

    /**
     * Queries for a specific, cross-origin bridge API receiver, using a specific DOM selector.
     *
     * @description This DOM selector is expected to be on the `<iframe>` markup of the element.
     *
     * @returns the window into the `<iframe>`, otherwise `undefined`.
     */
  }, {
    key: "getCrossOriginTarget",
    value: function getCrossOriginTarget(receiverId) {
      var frame = document.querySelector("[data-rovo-receiver-id=\"".concat(receiverId, "\"]"));
      if (!frame) {
        return;
      }
      return frame.contentWindow;
    }
  }]);
}();